import { useTranslation } from "react-i18next";

import { convertValueToLocale } from "helpers/convertValueToLocale";

import { Typography } from "shared/ui/components/Typography";

import { getChartData } from "./CalculatedData.helpers";

import styles from "./CalculatedData.module.scss";

const CalculatedData = ({
  proCalculationCharts,
  calculation,
  conclusionsPro,
}: {
  proCalculationCharts: any;
  calculation: any;
  conclusionsPro: any;
}) => {
  const { t } = useTranslation();

  const {
    earlySaleProfit,
    rentalYield,
    irr,
    roi,
    investmentsAcc,
    investorCashFlowAverage,
    specialLoan,
    totalTaxSavings,
  } = getChartData({
    proCalculationCharts,
    calculation,
    conclusionsPro,
  });

  const mapper = [10, 20, 30];

  return (
    <div className={styles.root}>
      <div className={styles.chartListItem}>
        <Typography
          variant="h5"
          weight="semiBold"
          className={styles.chartListText}
        >
          Prognose Vermögensentwicklung
        </Typography>

        <div className={styles.chartListInner}>
          {mapper.map((item) => (
            <Typography
              variant="bodyL"
              weight="semiBold"
              className={styles.chartListValue}
              key={item}
            >
              {`nach ${item} ${t("my_parameters.parameters.years")}`}
            </Typography>
          ))}
        </div>
      </div>

      {!!earlySaleProfit && (
        <div className={styles.chartListItem}>
          <Typography
            className={styles.chartListText}
            variant="bodyS"
            weight="regular"
          >
            {t("calculator_pro_page.analytics.early_sale_profit")}
          </Typography>

          <div className={styles.chartListInner}>
            {mapper.map((item) => (
              <Typography
                key={item}
                data-testid={"earlySaleProfit"}
                className={styles.chartListValue}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(earlySaleProfit[item], 0)}
              </Typography>
            ))}
          </div>
        </div>
      )}
      {!!rentalYield && (
        <div className={styles.chartListItem}>
          <Typography
            className={styles.chartListText}
            variant="bodyS"
            weight="regular"
          >
            {t("calculator_pro_page.analytics.rental_yield")}
          </Typography>

          <div className={styles.chartListInner}>
            {mapper.map((item) => (
              <Typography
                key={item}
                data-testid={"rentalYield"}
                className={styles.chartListValue}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(rentalYield, 2, "%")}
              </Typography>
            ))}
          </div>
        </div>
      )}
      {!!irr && (
        <div className={styles.chartListItem}>
          <Typography
            className={styles.chartListText}
            variant="bodyS"
            weight="regular"
          >
            {t("calculator_pro_page.analytics.irr")}
          </Typography>

          <div className={styles.chartListInner}>
            {mapper.map((item) => (
              <Typography
                key={item}
                data-testid={"irr"}
                className={styles.chartListValue}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(irr[item], 2, "%")}
              </Typography>
            ))}
          </div>
        </div>
      )}
      {!!roi && (
        <div className={styles.chartListItem}>
          <Typography
            className={styles.chartListText}
            variant="bodyS"
            weight="regular"
          >
            {t("calculator_pro_page.analytics.roi")}
          </Typography>

          <div className={styles.chartListInner}>
            {mapper.map((item) => (
              <Typography
                key={item}
                data-testid={"roi"}
                className={styles.chartListValue}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(roi[item], 2, "%")}
              </Typography>
            ))}
          </div>
        </div>
      )}
      {!!investmentsAcc && (
        <div className={styles.chartListItem}>
          <Typography
            className={styles.chartListText}
            variant="bodyS"
            weight="regular"
          >
            {t("calculator_pro_page.analytics.investments_acc")}
          </Typography>

          <div className={styles.chartListInner}>
            {mapper.map((item) => (
              <Typography
                key={item}
                data-testid={"investmentsAcc"}
                className={styles.chartListValue}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(Math.abs(investmentsAcc[item]), 0)}
              </Typography>
            ))}
          </div>
        </div>
      )}
      {!!investorCashFlowAverage && (
        <div className={styles.chartListItem}>
          <Typography
            className={styles.chartListText}
            variant="bodyS"
            weight="regular"
          >
            {t("calculator_pro_page.analytics.investor_cash_flow_average")}
          </Typography>

          <div className={styles.chartListInner}>
            {mapper.map((item) => (
              <Typography
                key={item}
                data-testid={"investorCashFlowAverage"}
                className={styles.chartListValue}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(investorCashFlowAverage[item])}
              </Typography>
            ))}
          </div>
        </div>
      )}

      {!!totalTaxSavings && (
        <div className={styles.chartListItem}>
          <Typography
            className={styles.chartListText}
            variant="bodyS"
            weight="regular"
          >
            {t("calculator_pro_page.analytics.total_tax_savings")}
          </Typography>

          <div className={styles.chartListInner}>
            {mapper.map((item) => (
              <Typography
                key={item}
                data-testid={"investorCashFlowAverage"}
                className={styles.chartListValue}
                variant="numbers"
                weight="medium"
              >
                {convertValueToLocale(Math.abs(totalTaxSavings[item]))}
              </Typography>
            ))}
          </div>
        </div>
      )}

      {!!specialLoan && (
        <div className={styles.chartListItem}>
          <Typography
            className={styles.chartListText}
            variant="bodyS"
            weight="regular"
          >
            {t("calculator_pro_page.analytics.special_loan")}
          </Typography>

          <div className={styles.chartListInner}>
            <Typography
              data-testid={"specialLoan"}
              className={styles.chartListValue}
              variant="numbers"
              weight="medium"
            >
              {convertValueToLocale(specialLoan)}
            </Typography>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalculatedData;
